import { getDownloadHeadersWithCurrentAccountConsumerId } from 'lib/downloads/utils';
import {
  DispatcherAuthPrefix,
  DispatcherCategoryService,
  DispatcherEndpoint,
  DispatcherResource
} from 'modules/types/common';
import { Dispatch } from 'redux';
import FileServices from 'services/FileServices';
import { CacheTypes } from 'modules/types/mobileCaching';
import { getDispatcherUrl } from 'modules/utils/UrlUtils';
import { DFDHeaders } from 'serverTypes/authentication';
import { messageShare } from 'store/messaging/actions';
import { RootState, Thunk } from 'store/types';
import {
  recentVitalsRequestStateSelector,
  vitalsDownloadingSelector,
  vitalsDownloadsByEncounterSelector
} from 'store/vitals/selectors';
import { TransmitSources } from '../transmit';
import { VitalsBlock } from './types';

export const GET_VITALS_CACHE_SUCCESS = 'VITALS.GET_VITALS_CACHE_SUCCESS';

export const GET_VITALS_DATA = 'VITALS.GET_VITALS_DATA';
export const GET_VITALS_DATA_SUCCESS = 'VITALS.GET_VITALS_DATA_SUCCESS';
export const GET_VITALS_DATA_FAIL = 'VITALS.GET_VITALS_DATA_FAIL';

export const GET_VITALS_BY_ENCOUNTER_ID = 'VITALS.GET_VITALS_BY_ENCOUNTER_ID';
export const GET_VITALS_BY_ENCOUNTER_ID_SUCCESS = 'VITALS.GET_VITALS_BY_ENCOUNTER_ID_SUCCESS';
export const GET_VITALS_BY_ENCOUNTER_ID_FAIL = 'VITALS.GET_VITALS_BY_ENCOUNTER_ID_FAIL';

export const GET_VITALS_BY_TYPE = 'VITALS.GET_VITALS_BY_TYPE';
export const GET_VITALS_BY_TYPE_SUCCESS = 'VITALS.GET_VITALS_BY_TYPE_SUCCESS';
export const GET_VITALS_BY_TYPE_FAIL = 'VITALS.GET_VITALS_BY_TYPE_FAIL';

export const getVitalsData = (useRequestState = true) => ({
  type: GET_VITALS_DATA,
  payload: {
    useRequestState,
    requestStateSelector: recentVitalsRequestStateSelector,
    cacheType: CacheTypes.VITALS,
    cacheRetrievalSuccessAction: GET_VITALS_CACHE_SUCCESS,
    client: 'dispatcher',
    request: {
      method: 'get',
      url: getDispatcherUrl(
        DispatcherCategoryService.HEALTH_RECORDS,
        DispatcherAuthPrefix.AUTHENTICATED,
        DispatcherResource.VITAL_SIGNS
      ),
      params: {}
    }
  }
});

export const SET_HISTORICAL_TYPE = 'SET_HISTORICAL_TYPE';
export const setHistoricalVitalType = (type: string) => ({
  type: SET_HISTORICAL_TYPE,
  payload: { type }
});

export const getVitalsByEncounterId = (encounterId: string) => ({
  type: GET_VITALS_BY_ENCOUNTER_ID,
  payload: {
    client: 'dispatcher',
    request: {
      method: 'get',
      url: getDispatcherUrl(
        DispatcherCategoryService.HEALTH_RECORDS,
        DispatcherAuthPrefix.AUTHENTICATED,
        DispatcherResource.VITAL_SIGNS
      ),
      headers: {
        [DFDHeaders.encounterId]: encounterId
      }
    }
  }
});

export const createReplyBodyFromVitalsBlock = (vitals: VitalsBlock[]) => {
  const twoLineSyntaxVital = vitals
    .map(
      vital =>
        `\nVital ${vital.title} value: ${vital.valueUnits[0].value} ${vital.valueUnits[0].units}${
          vital.valueUnits.length === 2 ? ', ' : ''
        }${
          vital.valueUnits.length === 2 ? vital.valueUnits[vital.valueUnits.length - 1].value : ''
        }${vital.valueUnits.length === 2 ? ' ' : ''}${
          vital.valueUnits.length === 2 ? vital.valueUnits[vital.valueUnits.length - 1].units : ''
        }\n(logged on ${vital.date} at Encounter ${vital.encounterId})`
    )
    .join(`,\n`);

  return twoLineSyntaxVital && twoLineSyntaxVital.length > 10
    ? twoLineSyntaxVital
    : 'Message details unavailable. Please try again.';
};

export const replyToVitals = (vitals: VitalsBlock[], returnBackTo?: string): Thunk => dispatch => {
  if (!vitals) {
    return;
  }

  const params = {
    subject: 'Vitals Question',
    readonly: createReplyBodyFromVitalsBlock(vitals),
    transmitParams: {
      source: TransmitSources.Vitals,
      payload: vitals,
      returnBackTo
    }
  };

  dispatch(messageShare(params));
};

export const SHARE_VITALS = 'VITALS.SHARE_VITALS';
export const SHARE_VITALS_SUCCESS = 'VITALS.SHARE_VITALS_SUCCESS';
export const SHARE_VITALS_FAIL = 'VITALS.SHARE_VITALS_FAIL';

export const shareVitals = (
  vitals: VitalsBlock,
  toAddress: string,
  message: string,
  useSecureEmail: boolean,
  contactPhoneNumber: string,
  subject: string
) => ({
  type: SHARE_VITALS,
  payload: {
    client: 'dispatcher',
    request: {
      method: 'post',
      url: getDispatcherUrl(
        DispatcherCategoryService.HEALTH_RECORDS,
        DispatcherAuthPrefix.AUTHENTICATED,
        DispatcherResource.VITAL_SIGNS,
        DispatcherEndpoint.TRANSMIT
      ),
      data: {
        toAddress,
        message,
        useSecureEmail,
        contactPhoneNumber,
        subject
      }
    }
  }
});

/**
 * Download Vitals
 */
export const DOWNLOAD_VITALS = 'VITALS.DOWNLOAD_VITALS';
export const DOWNLOAD_VITALS_SUCCESS = 'VITALS.DOWNLOAD_VITALS_SUCCESS';
export const DOWNLOAD_VITALS_FAIL = 'VITALS.DOWNLOAD_VITALS_FAIL';

export const downloadVitals = () => async (dispatch: Dispatch, getState: () => RootState) => {
  const state = getState();

  // Check for in flight downloads if there are any then return
  if (vitalsDownloadingSelector(state) === true) {
    return;
  }

  dispatch({ type: DOWNLOAD_VITALS, payload: {} });
  const timestamp = Date.now();
  const url = getDispatcherUrl(
    DispatcherCategoryService.HEALTH_RECORDS,
    DispatcherAuthPrefix.AUTHENTICATED,
    DispatcherResource.VITAL_SIGNS,
    DispatcherEndpoint.DOWNLOAD,
    true
  );

  try {
    const response = await FileServices.download(`vitals-${timestamp}.pdf`, url, {
      [DFDHeaders.accept]: 'application/pdf',
      ...getDownloadHeadersWithCurrentAccountConsumerId(state, url)
    });

    dispatch({ type: DOWNLOAD_VITALS_SUCCESS, payload: { response } });
  } catch (err) {
    dispatch({ type: DOWNLOAD_VITALS_FAIL, error: err });
  }
};

export const DOWNLOAD_VITALS_BY_ENCOUNTER = 'VITALS.DOWNLOAD_VITALS_BY_ENCOUNTER';
export const DOWNLOAD_VITALS_BY_ENCOUNTER_SUCCESS = 'VITALS.DOWNLOAD_VITALS_BY_ENCOUNTER_SUCCESS';
export const DOWNLOAD_VITALS_BY_ENCOUNTER_FAIL = 'VITALS.DOWNLOAD_VITALS_BY_ENCOUNTER_FAIL';

export const downloadVitalsByEncounter = (encounterId: string) => async (
  dispatch: Dispatch,
  getState: () => RootState
) => {
  const state = getState();

  // Check for in flight downloads if there are any then return
  if (vitalsDownloadsByEncounterSelector(state)[encounterId] === 'pending') {
    return;
  }

  dispatch({ type: DOWNLOAD_VITALS_BY_ENCOUNTER, payload: { id: encounterId } });
  const url = getDispatcherUrl(
    DispatcherCategoryService.HEALTH_RECORDS,
    DispatcherAuthPrefix.AUTHENTICATED,
    DispatcherResource.VITAL_SIGNS,
    DispatcherEndpoint.DOWNLOAD,
    true
  );

  try {
    const response = await FileServices.download(`vitals-${encounterId}.pdf`, url, {
      [DFDHeaders.accept]: 'application/pdf',
      [DFDHeaders.encounterId]: encounterId,
      ...getDownloadHeadersWithCurrentAccountConsumerId(state, url)
    });

    dispatch({
      type: DOWNLOAD_VITALS_BY_ENCOUNTER_SUCCESS,
      payload: { id: encounterId, response }
    });
  } catch (err) {
    dispatch({
      type: DOWNLOAD_VITALS_BY_ENCOUNTER_FAIL,
      payload: { id: encounterId },
      error: err
    });
  }
};
